
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { DomainRegistry } from "../../../domain";
import { ReceptionTodoSearchCondition } from "../../../domain/workflow/search";
import { ReceptionDetailSummary, ReceptionTodoListDispInfo } from "../../../domain/workflow/list";

import { default as WorkflowListBaseMixin } from "./WorkflowListBaseMixin.vue";
import { ReceptionDetailListColor, ReceptionListCardStyleDispInfo, WorkflowStatusName } from "../../../domain/workflow/items";
import { Patient } from "../../../domain/patient";
import { ReceptionReference } from "../../../domain/workflow/reference";
import { WorkflowDocumentKinds, WorkflowListSortItemKinds, TodoListTabKinds } from "../../../domain/workflow/kinds";
import { DocumentSummary } from "../../../domain/document";
import { IWorkflowTableSortParameter} from "../lists/tables/IWorkflowTableSortParameter";

@Component({
})

/**
 * ワークフロー一覧（記載者向け） 画面用Mixin
 */
export default class TodoListViewMixin extends Mixins(WorkflowListBaseMixin) {

  data(): any {
    return {
      displayKind: null,
      displaySetting: null as ReceptionTodoListDispInfo,

      myTodoSearchCondition: null,
      myDraftSearchCondition: null,
      myWrittenSearchCondition: null,
      deptTodoSearchCondition: null,
      backedTodoSearchCondition: null,

      myTodoSortParameter: null,
      myDraftSortParameter: null,
      myWrittenSortParameter: null,
      deptTodoSortParameter: null,
      backedTodoSortParameter: null,

      paramPatient: null,
    };
  }
  displayKind: TodoListTabKinds;
  displaySetting: ReceptionTodoListDispInfo;

  // 検索条件保存用（タブ切替・再検索用）
  myTodoSearchCondition: ReceptionTodoSearchCondition;
  myDraftSearchCondition: ReceptionTodoSearchCondition;
  myWrittenSearchCondition: ReceptionTodoSearchCondition;
  deptTodoSearchCondition: ReceptionTodoSearchCondition;
  backedTodoSearchCondition: ReceptionTodoSearchCondition;

  myTodoSortParameter: IWorkflowTableSortParameter;
  myDraftSortParameter: IWorkflowTableSortParameter;
  myWrittenSortParameter: IWorkflowTableSortParameter;
  deptTodoSortParameter: IWorkflowTableSortParameter;
  backedTodoSortParameter: IWorkflowTableSortParameter;
  paramPatient: Patient;

  async created() {
    await Promise.all([
      this.getDispSetting(),
      this.updateParamPatient()
    ]);
    this.$nextTick(() => this.setDefaultSelectedTab());
  }

  mounted() {
  }

  // -----------------------画面表示状態---------------------------

  get isDocumentListButtonVisible(): boolean {
    return this.displaySetting ? this.displaySetting.isDocumentListButtonEnable : false;
  }

  // 過去一覧画面表示
  showDocumentListView(): void {
    this.$openWindow("/ShowDocumentList");
  }

  get isDocumentCreateButtonVisible(): boolean {
    return this.displaySetting ? this.displaySetting.isDocumentCreateButtonEnable : false;
  }

  // 文書新規作成画面表示
  showSelectNewDocumentFormatView(): void {
    // 津山中央で緊急で必要になったので対応している
    const patientId = !!this.currentSearchPatient
      ? this.currentSearchPatient.id
      : null;
    this.$openWindow("/SelectNewDocumentFormat", { patientNo: patientId });
  }

  get currentSearchPatient(): Patient {
    const condition = this.getCurrentSearchCondition();
    return !!condition
      ? condition.patient
      : null;
  }

  get currentSearchPatientDescription(): string {
    return this.currentSearchPatient
      ? this.currentSearchPatient.id + "：" + this.currentSearchPatient.name
      : "患者未指定";
  }

  private getCurrentSearchCondition(): ReceptionTodoSearchCondition {
    if (this.isMyTodoTabVisible) {
      return this.myTodoSearchCondition;
    }
    if (this.isMyDraftTabVisible) {
      return this.myDraftSearchCondition;
    }
    if (this.isMyWrittenTabVisible) {
      return this.myWrittenSearchCondition;
    }
    if (this.isDeptTodoTabVisible) {
      return this.deptTodoSearchCondition;
    }
    if (this.isBackedTodoTabVisible) {
      return this.backedTodoSearchCondition;
    }
    return null;
  }

  get myWrittenDefaultSortParameter(): IWorkflowTableSortParameter {
    return {
      sortType: WorkflowListSortItemKinds.UpdateDate,
      orderType: "descending",
      sortConditionItems: [],
    };
  }

  getMyWrittenTabDefaultSortParameter(parameter: IWorkflowTableSortParameter): IWorkflowTableSortParameter {
    return parameter && this.isMultiConditionOrder(parameter)
      ? parameter
      : this.myWrittenDefaultSortParameter;
  }

  isMultiConditionOrder(parameter: IWorkflowTableSortParameter): boolean {
    return (!!parameter.sortConditionItems && parameter.sortConditionItems.length > 0)
      && (parameter.sortType === WorkflowListSortItemKinds.MultiCondition
          || parameter.sortType === Math.min(Math.max(parameter.sortType, WorkflowListSortItemKinds.PresetMin), WorkflowListSortItemKinds.PresetMax));
  }

  initializeSortParameter(parameter: IWorkflowTableSortParameter): void {
    this.myTodoSortParameter = parameter;
    this.myDraftSortParameter = parameter;
    this.myWrittenSortParameter = this.getMyWrittenTabDefaultSortParameter(parameter); // 最終更新依頼タブだけ初期値が最終更新日の降順(プリセットがあればプリセット優先)
    this.deptTodoSortParameter = parameter;
    this.backedTodoSortParameter = parameter;
  }

  // ------------------タブ表示状態--------------------------

  get isDeptTodoTabEnable(): boolean {
    return this.$configuration.behavior.reception.list.tabs.departmentTodo.isEnable;
  }

  get isMyDraftTabEnable(): boolean {
    return this.displaySetting && this.displaySetting.myDraftTabEnable;
  }

  get isMyWrittenTabEnable(): boolean {
    return !!this.displaySetting && this.displaySetting.myWrittenTabEnable;
  }

  get defaultSelectedTabSetting(): TodoListTabKinds {
    return this.displaySetting
      ? this.displaySetting.defaultSelectedTodoListTab
      : TodoListTabKinds.Myself;
  }

  get cardStyleDisplayInfo(): ReceptionListCardStyleDispInfo {
    return this.displaySetting
      ? this.displaySetting.cardStyle
      : null;
  }

  // MyTodoタブ表示
  showMyTodoTab(): void {
    this.displayKind = TodoListTabKinds.Myself;
  }

  // 下書きタブ表示
  showMyDraftTab(): void {
    this.displayKind = TodoListTabKinds.MyDraft;
  }

  // 対応済みタブ表示
  showMyWrittenTab(): void {
    this.displayKind = TodoListTabKinds.MyWritten;
  }

  // 所属診療科Todoタブ表示
  showDeptTodoTab(): void {
    this.displayKind = TodoListTabKinds.Department;
  }

  // 差戻しタブ表示
  showBackedTodoTab(): void {
    this.displayKind = TodoListTabKinds.Backed;
  }

  get isMyTodoTabVisible(): boolean {
    return this.displayKind === TodoListTabKinds.Myself;
  }

  get isMyDraftTabVisible(): boolean {
    return this.displayKind === TodoListTabKinds.MyDraft;
  }

  get isMyWrittenTabVisible(): boolean {
    return this.displayKind === TodoListTabKinds.MyWritten;
  }

  get isDeptTodoTabVisible(): boolean {
    return this.displayKind === TodoListTabKinds.Department;
  }

  get isBackedTodoTabVisible(): boolean {
    return this.displayKind === TodoListTabKinds.Backed;
  }

  get myTodoConditionSummary(): string {
    return this.myTodoSearchCondition ? this.myTodoSearchCondition.getSummary(this.displaySetting.labelSetting.generalFlag1LabelName) : "";
  }

  get myDraftConditionSummary(): string {
    return this.myDraftSearchCondition ? this.myDraftSearchCondition.getSummary(this.displaySetting.labelSetting.generalFlag1LabelName) : "";
  }

  get myWrittenConditionSummary(): string {
    return this.myWrittenSearchCondition ? this.myWrittenSearchCondition.getSummary(this.displaySetting.labelSetting.generalFlag1LabelName) : "";
  }

  get departmentTodoConditionSummary(): string {
    return this.deptTodoSearchCondition ? this.deptTodoSearchCondition.getSummary(this.displaySetting.labelSetting.generalFlag1LabelName) : "";
  }

  get backedTodoConditionSummary(): string {
    return this.backedTodoSearchCondition ? this.backedTodoSearchCondition.getSummary(this.displaySetting.labelSetting.generalFlag1LabelName) : "";
  }

  get displayColor(): ReceptionDetailListColor {
    return this.displaySetting ? this.displaySetting.displayColor : null;
  }

  get draftTabName(): string {
    return this.draftStatus ? this.draftStatus.waitName : "";
  }

  get draftStatus(): WorkflowStatusName {
    const statuses = this.displaySetting ? this.displaySetting.workflowStatuses : [];
    // TODO: バックエンド・フロントエンドの処理共にステータスを決め打ちしているのはやめたほうがよいように見える
    return Enumerable.from(statuses).firstOrDefault(s => s.code === 200);
  }

  get writtenTabName(): string {
    // WebWorkflowでは設定可能だが、V3では必要になるまで固定の文言で対応する（おそらく対応不要な見込み）
    return "最終更新依頼";
  }

  private setDefaultSelectedTab(): void {
    this.displayKind = this.isEnableTab(this.defaultSelectedTabSetting)
      ? this.defaultSelectedTabSetting
      : TodoListTabKinds.Myself;
  }

  private isEnableTab(tab: TodoListTabKinds): boolean {
    switch (tab) {
      case TodoListTabKinds.MyDraft:
        return this.isMyDraftTabEnable;
      case TodoListTabKinds.MyWritten:
        return this.isMyWrittenTabEnable;
      case TodoListTabKinds.Department:
        return this.isDeptTodoTabEnable;
      default:
        return true;
    }
  }

  // ---------------------Emitで呼ばれる--------------------------------

  // 依頼削除通知を受け取りました
  onDeletedReceptionDetail() {
    this.$logger.debug("onDeletedReceptionDetail");

    this.reference = null;
    this.refreshSearch();
  }

  // WF実行通知を受け取りました
  onDoActionedReceptionDetail() {
    this.$logger.debug("onDoActionedReceptionDetail");

    this.reference = null;
    this.refreshSearch();
  }

  // 依頼書削除通知を受け取りました
  onRequestSheetDeleted() {
    this.$logger.debug("onRequestSheetDeleted");
    this.reloadDetail();
  }

  // MyTodo検索依頼が届きました
  onExecMyTodoSearch(condition: ReceptionTodoSearchCondition): void {
    this.$logger.debug("onExecMyTodoSearch");
    this.myTodoSearchCondition = condition.clone();
    this.searchTodoMyself(true);
  }

  // 下書き検索依頼が届きました
  onExecMyDraftSearch(condition: ReceptionTodoSearchCondition): void {
    this.$logger.debug("onExecMyDraftSearch");
    this.myDraftSearchCondition = condition.clone();
    this.searchMyDraft(true);
  }

  // 対応済み検索依頼が届きました
  onExecMyWrittenSearch(condition: ReceptionTodoSearchCondition): void {
    this.$logger.debug("onExecMyWrittenSearch");
    this.myWrittenSearchCondition = condition.clone();
    this.searchMyWritten(true);
  }

  // 所属診療科Todo検索依頼が届きました
  onExecDepartmentTodoSearch(condition: ReceptionTodoSearchCondition): void {
    this.$logger.debug("onExecDeptTodoSearch");
    this.deptTodoSearchCondition = condition.clone();
    this.searchTodoDepartment(true);
  }

  // 差戻しTodo検索依頼が届きました
  onExecBackedTodoSearch(condition: ReceptionTodoSearchCondition): void {
    this.$logger.debug("onExecBackedTodoSearch");
    this.backedTodoSearchCondition = condition.clone();
    this.searchTodoBacked(true);
  }

  // 子どもから汎用フラグ更新通知がきました
  onGeneralFlagUpdated(): void {
    this.$logger.debug("onGeneralFlagUpdated");
    this.refreshSearch();
  }

  onOrderChanged(changedParameter: IWorkflowTableSortParameter): void {
    if (this.isMyTodoTabVisible) {
      this.myTodoSortParameter = changedParameter;
    }
    if (this.isMyDraftTabVisible) {
      this.myDraftSortParameter = changedParameter;
    }
    if (this.isMyWrittenTabVisible) {
      this.myWrittenSortParameter = changedParameter;
    }
    if (this.isDeptTodoTabVisible) {
      this.deptTodoSortParameter = changedParameter;
    }
    if (this.isBackedTodoTabVisible) {
      this.backedTodoSortParameter = changedParameter;
    }
  }

  // --------------------メソッド（$refでも呼ばれる）---------------------------------

  // 再検索
  refreshSearch(shouldSelectFirst: boolean = false): void {
    this.$logger.debug("exec refreshTodoSearch..");

    // MyTodoタブ選択状態か
    if (this.isMyTodoTabVisible) {
      if (!this.myTodoSearchCondition) {
        console.log("refreshSearch. myTodoCondition is nothing.");
        return;
      }
      this.searchTodoMyself(shouldSelectFirst);
      return;
    }

    // 下書きタブ選択状態か
    if (this.isMyDraftTabVisible) {
      if (!this.myDraftSearchCondition) {
        console.log("refreshSearch. myDraftCondition is nothing.");
        return;
      }
      this.searchMyDraft(shouldSelectFirst);
      return;
    }

    // 対応済みタブ選択状態か
    if (this.isMyWrittenTabVisible) {
      if (!this.myWrittenSearchCondition) {
        console.log("refreshSearch. myWrittenCondition is nothing.");
        return;
      }
      this.searchMyWritten(shouldSelectFirst);
      return;
    }

    // 所属部署タブ選択状態か
    if (this.isDeptTodoTabVisible) {
      if (!this.deptTodoSearchCondition) {
        console.log("refreshSearch. deptTodoCondition is nothing.");
        return;
      }
      this.searchTodoDepartment(shouldSelectFirst);
      return;
    }

    // 差戻しタブ選択状態か
    if (this.isBackedTodoTabVisible) {
      if (!this.backedTodoSearchCondition) {
        console.log("refreshSearch. backedTodoCondition is nothing.");
        return;
      }
      this.searchTodoBacked(shouldSelectFirst);
      return;
    }
  }
  
  
  // --------------------API通信-----------------------------------

  // 表示設定取得
  async getDispSetting(): Promise<void> {
    try {
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.getReceptionTodoListInitSetting();
      this.isLoading = false;

      this.$logger.debug("displaySetting is set.");
      this.displaySetting = result;
    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "表示設定の取得に失敗しました。", e);
    }
  }

  // MyTodo検索
  async searchTodoMyself(shouldSelectFirst: boolean): Promise<void> {
    try {

      this.clearSearchResult();     
      
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.searchReceptionTodoMyself(this.myTodoSearchCondition);
      this.overLimitMessage = result.overLimitMessage;
      this.setDetailList(result.list, shouldSelectFirst);
      this.isLoading = false;

    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "MyTodo依頼一覧の取得に失敗しました。", e);
    }
  }

  // 下書き検索
  async searchMyDraft(shouldSelectFirst: boolean): Promise<void> {
    try {

      this.clearSearchResult();     
      
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.searchReceptionTodoMyself(this.myDraftSearchCondition);
      this.overLimitMessage = result.overLimitMessage;
      this.setDetailList(result.list, shouldSelectFirst);
      this.isLoading = false;

    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "下書き依頼一覧の取得に失敗しました。", e);
    }
  }

  // 対応済み検索
  async searchMyWritten(shouldSelectFirst: boolean): Promise<void> {
    try {

      this.clearSearchResult();     
      
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.searchReceptionTodoMyWritten(this.myWrittenSearchCondition);
      this.overLimitMessage = result.overLimitMessage;
      this.setDetailList(result.list, shouldSelectFirst);
      this.isLoading = false;

    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "対応済み依頼一覧の取得に失敗しました。", e);
    }
  }

  // 所属診療科Todo検索
  async searchTodoDepartment(shouldSelectFirst: boolean): Promise<void> {
    try {

      this.clearSearchResult();
      
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.searchReceptionTodoDepartment(this.deptTodoSearchCondition);
      this.overLimitMessage = result.overLimitMessage;
      this.setDetailList(result.list, shouldSelectFirst);
      this.isLoading = false;

    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "所属部署依頼一覧の取得に失敗しました。", e);
    }
  }

  // 差戻しTodo検索
  async searchTodoBacked(shouldSelectFirst: boolean): Promise<void> {
    try {

      this.clearSearchResult();
      
      const repository = DomainRegistry.workflow.getWorkflowRepository();
      this.isLoading = true;
      const result = await repository.searchReceptionTodoBacked(this.backedTodoSearchCondition);
      this.overLimitMessage = result.overLimitMessage;
      this.setDetailList(result.list, shouldSelectFirst);
      this.isLoading = false;

    } catch (e) {
      this.isLoading = false;
      this.$error("エラー", "差戻し依頼一覧の取得に失敗しました。", e);
    }
  }

  // 患者情報を取得
  async getPatientInfo(patientNo: string): Promise<Patient> {
    if (!patientNo) { return null; }
    try {
      const repository = DomainRegistry.patient.getPatientRepository();
      return await repository.getEntity(patientNo);
    } catch (e) {
      this.$error("エラー", "指定患者の取得に失敗しました。", e);
      return null;
    }
  }

  // --------------------ダブルクリック時の処理（暫定対応）-----------------------------------

  // TODO: refactor
  // WorkflowListViewCardStyle・WorkflowListTableにも同じような処理がある
  // 全く同じで処理ではないので共通化は未了
  // ↓ここから

  async doDoubleClickAction(row: ReceptionDetailSummary): Promise<void> {
    if (!row) { return; }

    if (this.isLoading || !this.reference || !this.reference.receptionDetail || row.id !== this.reference.receptionDetail.id) {
      console.debug("not equal receptionDetailId.");
      const reference = await this.getDetailWithoutLoading(row.id);
      if (!reference) { return; }
      this.doDoubleClickDetailAction(reference);
      return;
    }

    this.doDoubleClickDetailAction(this.reference);
  }

  private doDoubleClickDetailAction(reference: ReceptionReference): void {
    if (reference.displayInfo.permissions.documentQuote.isPermitted) {
      // 文書作成（引用）
      this.createQuotedDocument(reference);
    } else if (reference.displayInfo.permissions.documentCreate.isPermitted) {
      // 文書作成
      this.createDocument(reference);
    } else if (reference.displayInfo.permissions.documentEdit.isPermitted) {
      // 文書編集
      this.editWorkflowDocument(reference);
    }
  }

  // 文書作成（引用）
  async createQuotedDocument(reference: ReceptionReference): Promise<void> {
    // 権限チェックする
    const result = await this.checkDocumentPermission(reference.quoteSourceDocument);
    if (!result) { return; }

    const param: any = {
      documentKind: WorkflowDocumentKinds.Document,
      receptionDetailId: reference.receptionDetail.id,
      formatId: reference.receptionDetail.format.id,
      patientNo: reference.reception.patientInfo.patient.id,
      quotedDocumentId: reference.quoteSourceDocument.id,
    };

    this.$openWindow("/EditNewQuotedWorkflowDocument", param);
  }

  createDocument(reference: ReceptionReference): void {
    const param: any = {
      documentKind: WorkflowDocumentKinds.Document,
      receptionDetailId: reference.receptionDetail.id,
      formatId: reference.receptionDetail.format.id,
      patientNo: reference.reception.patientInfo.patient.id
    };

    this.$openWindow("/EditNewWorkflowDocument", param);
  }

  private async editWorkflowDocument(reference: ReceptionReference): Promise<void> {

    const result = await this.checkDocumentPermission(reference.document);
    if (!result) { return; }

    const param: any = {
      documentKind: WorkflowDocumentKinds.Document,
      receptionDetailId: reference.receptionDetail.id,
      patientNo: reference.reception.patientInfo.patient.id,
      documentId: reference.document.id,
      isReadRestrictionAlreadyHandled: true.toString(),
    };

    this.$openWindow("/EditWorkflowDocument", param);
  }
  
  async checkDocumentPermission(document: DocumentSummary): Promise<boolean> {
    try {
      // TODO: 現状では参照不可の文書でここに到達することは無いようだが、isReadableの判定もしておいたほうがよい？
      const result = await this.isConfirmedReadable(document);
      if (!result) { return false; }
      await this.checkLogin(document);
      await this.checkWriteAuditLog(document);

      return true;
    } catch (e) {
      debugger;
      // confirm で いいえを押すと飛んでくる？
      console.log("dialog cancel?");
      return false;
    }
  }

  async isConfirmedReadable(document: DocumentSummary): Promise<boolean> {
    if (!document.shouldConfirmReadable) { return true; }
    const index = await this.$commonDialogs.confirmation.confirmDangerCancel(
      null,
      document.shouldWriteAuditLog
        ? ["この書類の閲覧は許可されていません。閲覧履歴は管理者に通知されます。", "処理を続行しますか？"]
        : ["この書類の閲覧は許可されていません。処理を続行しますか？"],
      "続行する"
    );
    return (index === 0);
  }

  async checkLogin(document: DocumentSummary): Promise<void> {
    console.log("checkLogin");
    if (document.shouldLogin) {
      await this.$commonDialogs.authentication.authenticator.show();
    }
  }

  async checkWriteAuditLog(document: DocumentSummary): Promise<void> {
    if (document.shouldWriteAuditLog) {
      try {
        const repository = DomainRegistry.document.getDocumentRepository();
        await repository.registerAuditLog(document);
      } catch (e) {
        this.$error(
          "閲覧履歴通知失敗",
          "閲覧履歴通知でエラーが発生しました。",
          e,
        );
        throw e;
      }
    }
  }

  // WorkflowListViewCardStyle・WorkflowListTableにも同じような処理がある
  // 全く同じで処理ではないので共通化は未了
  // ↑ここまで

  async updateParamPatient(): Promise<void> {
    this.paramPatient = await this.getPatientInfo(this.paramPatientNo);
  }
}
